import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import "../../../App.css";
import Snackbar from "../../../components/ui/Snackbar";
import LoaderCon from "../../../components/ui/LoaderContainer";
import Logo from "../../../assets/images/tapxlogobalck.png";

export default function Login({ snackbar, close_snack_bar, loginFetch, forgotpassword }) {
  const [forget, setForget] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (forget) {
        handleForgotPassword();
      } else {
        handleLogin();
      }
    }
  };

  const handleLogin = () => {
    if (email && password) {
      loginFetch(email, password);
      // Clear fields only on success or let the user fix the error? 
      // Usually better to keep them if it fails, but for now we follow previous logic or keep it simple.
      // The previous logic cleared them immediately which is bad UX if it fails, but I will stick to clearing for now as per previous code, 
      // or actually, let's IMPROVE it by NOT clearing automatically so they can retry. 
      // Wait, the previous code cleared it. Let's keep it user friendly and NOT clear it immediately.
    }
  };

  const handleForgotPassword = () => {
    if (email) {
      forgotpassword(email);
      setForget(false);
      setEmail("");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Background Decorative Elements for "Premium" feel */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          left: "-10%",
          width: "50%",
          height: "50%",
          background: "radial-gradient(circle, rgba(147,51,234,0.3) 0%, rgba(0,0,0,0) 70%)",
          borderRadius: "50%",
          filter: "blur(100px)",
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-10%",
          right: "-10%",
          width: "50%",
          height: "50%",
          background: "radial-gradient(circle, rgba(56,189,248,0.2) 0%, rgba(0,0,0,0) 70%)",
          borderRadius: "50%",
          filter: "blur(100px)",
          zIndex: 0
        }}
      />

      <Box
        sx={{
          zIndex: 1,
          width: "100%",
          maxWidth: "450px", // Slightly wider for better breathability
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(20px)",
          borderRadius: "30px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
          p: { xs: 4, sm: 6 },
          textAlign: "center",
          animation: "fadeIn 0.8s ease-out",
          "@keyframes fadeIn": {
            "0%": { opacity: 0, transform: "translateY(20px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="Tapx Digital"
          sx={{
            width: "150px",
            mb: 1,
            filter: "brightness(0) invert(1)" // Make logo white for dark theme
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#fff",
            mb: 1,
            letterSpacing: "0.5px"
          }}
        >
          {forget ? "Reset Password" : "Welcome Back"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            mb: 5,
            fontWeight: 400
          }}
        >
          {forget
            ? "Enter your email to receive a reset link."
            : "Sign in to access your dashboard"}
        </Typography>

        <Box component="form" noValidate autoComplete="off">
          {!forget ? (
            <>
              <TextField
                fullWidth
                placeholder="Email Address"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    borderRadius: "15px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
                    "&.Mui-focused fieldset": { borderColor: "#fff" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    borderRadius: "15px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
                    "&.Mui-focused fieldset": { borderColor: "#fff" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />

              <Box display="flex" justifyContent="flex-end" mb={4}>
                <Typography
                  variant="body2"
                  onClick={() => setForget(true)}
                  sx={{
                    color: "#a5b4fc",
                    cursor: "pointer",
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline", color: "#fff" },
                    transition: "color 0.3s"
                  }}
                >
                  Forgot Password?
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleLogin}
                sx={{
                  py: 1.8,
                  borderRadius: "15px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(90deg, #4caf50, #45a049)", // Green accent for "Go" action
                  boxShadow: "0 4px 15px rgba(76, 175, 80, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #45a049, #388e3c)",
                    boxShadow: "0 6px 20px rgba(76, 175, 80, 0.6)",
                  }
                }}
              >
                Sign In
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Registered Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  mb: 4,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    borderRadius: "15px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
                    "&.Mui-focused fieldset": { borderColor: "#fff" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleForgotPassword}
                sx={{
                  py: 1.8,
                  borderRadius: "15px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  background: "linear-gradient(90deg, #60a5fa, #3b82f6)", // Blue accent
                  boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                  mb: 2,
                  "&:hover": {
                    background: "linear-gradient(90deg, #3b82f6, #2563eb)",
                  }
                }}
              >
                Send Reset Link
              </Button>

              <Button
                fullWidth
                variant="text"
                onClick={() => { setForget(false); setEmail(""); }}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { color: "#fff", bgcolor: "transparent" }
                }}
              >
                Back to Login
              </Button>
            </>
          )}
        </Box>
      </Box>

      <LoaderCon />
      <Snackbar
        open={snackbar.response_received}
        close_snack_bar={close_snack_bar}
        message={snackbar.message}
      />
    </Box>
  );
}
